import { useState } from 'react';

import { Button } from '@/components/ui/button';
import type { WorkflowRelatedGroup } from '@/lib/api/generated/model';
import { useRelatedTaskSiblings } from '../hooks/useRelatedTaskSiblings';
import { useRelatedTaskLink } from '../navigation';
import { relationLabel, type RelationRowModel } from '../types';
import { RelationRow } from './RelationRow';

export interface WiderWorkflowGroupProps {
  workspaceId: string;
  taskId: string;
  group: WorkflowRelatedGroup;
  /** Titles by task id, for naming the task a row is reached through. */
  titlesById: ReadonlyMap<string, string>;
}

/**
 * One workflow's share of the tasks reached through other tasks.
 *
 * Its siblings — tasks in the same workflow that are neither above nor below the
 * current one — stay a separate opt-in, read only when this group's own control
 * is used (spec 100 FR-020). The hook is already gated by a flag, so the whole
 * of that laziness is the `showSiblings` boolean below. The surface this
 * replaces lifted sibling data to a parent through an effect fired by a
 * component that rendered nothing, because it drew siblings in two places at
 * once; one surface needs none of that.
 */
export function WiderWorkflowGroup({
  workspaceId,
  taskId,
  group,
  titlesById,
}: WiderWorkflowGroupProps) {
  const [showSiblings, setShowSiblings] = useState(false);
  const siblings = useRelatedTaskSiblings(
    workspaceId,
    taskId,
    group.workflow_id,
    showSiblings,
  );
  const relatedTaskLink = useRelatedTaskLink(workspaceId);

  // Depth 1 is already listed above as one of the task's own connections;
  // repeating it here would show the same task twice with two different
  // affordances.
  const rows: RelationRowModel[] = group.tasks
    .filter((item) => item.depth > 1)
    .map((item) => ({
      key: item.task_id,
      title: item.title,
      context: item.step_name,
      label: relationLabel(item.relation_type),
      link: relatedTaskLink(group.workflow_id, item.task_id),
      distance: item.depth,
      viaTitle: item.via_task_id
        ? (titlesById.get(item.via_task_id) ?? null)
        : null,
      // Not this task's connection to remove: the payload carries no relation
      // id for it, because the edge belongs to two other tasks (FR-019).
      onRemove: null,
    }));

  const siblingRows: RelationRowModel[] = (siblings.data?.siblings ?? []).map(
    (item) => ({
      key: `sibling:${item.task_id}`,
      title: item.title,
      context: item.step_name,
      label: 'Sibling',
      link: relatedTaskLink(group.workflow_id, item.task_id),
      distance: null,
      viaTitle: null,
      onRemove: null,
    }),
  );

  const siblingsAvailable = (group.siblings_available ?? 0) > 0;

  return (
    <section
      aria-labelledby={`wider-${group.workflow_id}`}
      className="flex min-w-0 flex-col gap-1.5"
    >
      <div className="flex min-w-0 items-center justify-between gap-2">
        <h4
          id={`wider-${group.workflow_id}`}
          className="text-muted-foreground min-w-0 truncate text-xs font-medium"
        >
          {group.workflow_name}
        </h4>
        {siblingsAvailable ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-pressed={showSiblings}
            onClick={() => setShowSiblings((shown) => !shown)}
          >
            {showSiblings ? 'Hide siblings' : 'Show siblings'}
          </Button>
        ) : null}
      </div>

      {rows.length === 0 ? (
        <p className="text-muted-foreground text-xs">
          No task reached through another here.
        </p>
      ) : (
        <ul className="flex min-w-0 flex-col gap-1">
          {rows.map((row) => (
            <RelationRow key={row.key} row={row} />
          ))}
        </ul>
      )}

      {showSiblings ? (
        <ul className="border-border flex min-w-0 flex-col gap-1 border-l pl-2">
          {siblings.isPending ? (
            <li className="text-muted-foreground px-2 py-1 text-xs">
              Loading…
            </li>
          ) : siblingRows.length === 0 ? (
            <li className="text-muted-foreground px-2 py-1 text-xs">
              No other task in this workflow.
            </li>
          ) : (
            siblingRows.map((row) => <RelationRow key={row.key} row={row} />)
          )}
        </ul>
      ) : null}
    </section>
  );
}
